import { Component } from '@theme/component';
import { debounce, isClickedOutside } from '@theme/utilities';

const CLOSE_ANIMATION_FALLBACK_MS = 420;
const OPEN_EVENT_DELAY_MS = 0;
const IGNORE_OUTSIDE_CLICK_AFTER_OPEN_MS = 420;

function waitForDialogCloseAnimation(dialog) {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    let resolved = false;
    let fallbackTimer = 0;

    const finish = () => {
      if (resolved) return;
      resolved = true;

      window.clearTimeout(fallbackTimer);
      dialog.removeEventListener('animationend', handleEnd);
      dialog.removeEventListener('transitionend', handleEnd);
      resolve();
    };

    const handleEnd = (event) => {
      if (event.target !== dialog) return;
      finish();
    };

    dialog.addEventListener('animationend', handleEnd);
    dialog.addEventListener('transitionend', handleEnd);
    fallbackTimer = window.setTimeout(finish, CLOSE_ANIMATION_FALLBACK_MS);
  });
}

export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.clearTimeout(this.#openEventTimer);
    this.#openEventTimer = 0;

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;
    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;

    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;
  #previousBodyPaddingRight = '';
  #appliedScrollbarCompensation = false;
  #openEventTimer = 0;
  #isClosing = false;
  #ignoreOutsideClickUntil = 0;

  #now() {
    if (window.performance && typeof window.performance.now === 'function') {
      return window.performance.now();
    }

    return Date.now();
  }

  #getOpenState(dialog) {
    const skipScrollbarCompensation = dialog.hasAttribute('data-eg-no-scrollbar-compensation');
    const documentElement = document.documentElement;
    const viewportWidth = window.innerWidth || documentElement.clientWidth || 0;
    const layoutWidth = documentElement.clientWidth || viewportWidth;
    const scrollbarWidth = skipScrollbarCompensation ? 0 : Math.max(0, viewportWidth - layoutWidth);
    const previousBodyPaddingRight = document.body.style.paddingRight || '';
    const inlineBodyPaddingRight = parseFloat(previousBodyPaddingRight) || 0;

    return {
      scrollY: window.scrollY || window.pageYOffset || 0,
      scrollbarWidth,
      previousBodyPaddingRight,
      nextBodyPaddingRight: scrollbarWidth > 0 ? `${inlineBodyPaddingRight + scrollbarWidth}px` : '',
    };
  }

  #dispatchOpenEventSoon() {
    window.clearTimeout(this.#openEventTimer);

    this.#openEventTimer = window.setTimeout(() => {
      this.#openEventTimer = 0;

      const { dialog } = this.refs;
      if (!dialog.open || this.#isClosing) return;

      this.dispatchEvent(new DialogOpenEvent());
    }, OPEN_EVENT_DELAY_MS);
  }

  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open || this.#isClosing) return;

    const openState = this.#getOpenState(dialog);

    this.#previousScrollY = openState.scrollY;
    this.#previousBodyPaddingRight = openState.previousBodyPaddingRight;
    this.#appliedScrollbarCompensation = openState.scrollbarWidth > 0;
    this.#ignoreOutsideClickUntil = this.#now() + IGNORE_OUTSIDE_CLICK_AFTER_OPEN_MS;

    if (this.#appliedScrollbarCompensation) {
      document.body.style.paddingRight = openState.nextBodyPaddingRight;
    }

    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${openState.scrollY}px`;

    try {
      dialog.showModal();
    } catch (_) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';

      if (this.#appliedScrollbarCompensation) {
        document.body.style.paddingRight = this.#previousBodyPaddingRight;
        this.#appliedScrollbarCompensation = false;
      }

      this.#ignoreOutsideClickUntil = 0;
      return;
    }

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeyDown);
    this.#dispatchOpenEventSoon();
  }

  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open || this.#isClosing) return;

    this.#isClosing = true;
    this.#ignoreOutsideClickUntil = 0;

    window.clearTimeout(this.#openEventTimer);
    this.#openEventTimer = 0;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    const skipCloseAnimation = dialog.hasAttribute('data-eg-skip-close-animation');

    if (!skipCloseAnimation) {
      dialog.classList.add('dialog-closing');

      try {
        await waitForDialogCloseAnimation(dialog);
      } catch (_) {}
    }

    try {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';

      if (this.#appliedScrollbarCompensation) {
        document.body.style.paddingRight = this.#previousBodyPaddingRight;
        this.#appliedScrollbarCompensation = false;
      }

      try {
        window.scrollTo({ top: this.#previousScrollY, behavior: 'auto' });
      } catch (_) {
        try {
          window.scrollTo(0, this.#previousScrollY);
        } catch (_) {}
      }
    } finally {
      try {
        if (dialog.open) dialog.close();
      } catch (_) {}

      dialog.classList.remove('dialog-closing');
      dialog.removeAttribute('data-eg-skip-close-animation');

      this.#isClosing = false;
      this.dispatchEvent(new DialogCloseEvent());
    }
  };

  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  #handleClick(event) {
    const { dialog } = this.refs;
    if (!dialog.open || this.#isClosing) return;

    const clickedOutside = isClickedOutside(event, dialog);
    if (!clickedOutside) return;

    if (this.#now() < this.#ignoreOutsideClickUntil) {
      event.preventDefault();
      event.stopPropagation();

      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      return;
    }

    this.closeDialog();
  }

  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) {
  customElements.define('dialog-component', DialogComponent);
}

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName, { bubbles: true, composed: true });
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName, { bubbles: true, composed: true });
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);